/**
 * Root error boundary page for React Router.
 * SECURITY: In production, no error details (message, status, stack) are rendered
 * to avoid leaking implementation or internal paths (CWE-209, CWE-497).
 * ASSUMPTION: Environment is determined at build time via import.meta.env (Vite)
 * production builds must set PROD=true by running `vite build --mode production`.
 */

import { useRouteError, useLocation } from "react-router";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import { AlertCircle, Copy, Home, RefreshCw, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { getDefaultRoute } from "@/routes/loaders";
import DefaultStore from "@/store/Store";
import { IS_PROD } from "./utils/env";

// SECURITY: Treat as production when PROD is set (Vite) or NODE_ENV is production (CRA).
// RISK: If env is misconfigured, dev details could leak in production; ensure build pipeline sets env correctly.
const isProduction = IS_PROD;

const ErrorPage = () => {
  const error = useRouteError();
  const location = useLocation();
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  // SECURITY: Log only in development; run in an effect so logging is not tied to render passes.
  useEffect(() => {
    if (!isProduction && error) {
      console.error("[Error boundary]", error);
    }
  }, [error]);

  const statusCode = error && typeof error.status === "number" ? error.status : null;
  const statusText = error && typeof error.statusText === "string" ? error.statusText : null;
  const message = error && typeof error.message === "string" ? error.message : error?.data?.message || null;
  const stack = error && typeof error.stack === "string" ? error.stack : null;

  const goHome = () => {
    const authStore = DefaultStore.authStore;
    const target = authStore?.userId ? getDefaultRoute(authStore) : "/signin";
    // ASSUMPTION: After route errorElement is shown, client navigate() may not recover (RR #10157); hard navigation resets router state.
    window.location.assign(target);
  };
  const retry = () => window.history.go(0);

  const handleCopy = () => {
    const textToCopy = `Error: ${message}\nStatus: ${statusCode} ${statusText}\nURL: ${window.location.href}\n\nStack Trace:\n${stack}`;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopySnackbarOpen(true);
    });
  };

  const handleSearch = () => {
    if (message) {
      const query = encodeURIComponent(`react ${message}`);
      window.open(`https://www.google.com/search?q=${query}`, "_blank");
    }
  };

  return (
    <Box
      component="main"
      id="error-page"
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "var(--background, #fefefe)",
        p: 2,
      }}
      role="alert"
      aria-live="assertive"
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 900,
          width: "100%",
          p: 4,
          textAlign: "center",
          border: "1px solid var(--border, #f5f5f5)",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            color: "var(--destructive, #ac2330)",
            mb: 2,
          }}
          aria-hidden
        >
          <AlertCircle size={56} strokeWidth={1.25} />
        </Box>
        <Typography
          component="h1"
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "var(--foreground, #2d3748)",
            mb: 1,
          }}
        >
          Something went wrong
        </Typography>
        <Typography variant="body1" sx={{ color: "var(--gray-500, #555)", mb: 3 }}>
          We couldn’t complete your request. Please try again or return to the home page.
        </Typography>

        {/* SECURITY: Technical details only in non-production to avoid information disclosure. */}
        {!isProduction && (message || statusCode != null || stack) && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              textAlign: "left",
              bgcolor: "var(--gray-150, #f5f5f5)",
              borderColor: "var(--gray-300, #d4d4d4)",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "var(--gray-700)", fontWeight: 600 }}>
                Technical details (development only)
              </Typography>
              <Box>
                <Tooltip title="Copy error details">
                  <IconButton size="small" onClick={handleCopy} aria-label="Copy error details">
                    <Copy size={18} strokeWidth={2} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Search on Google">
                  <IconButton size="small" onClick={handleSearch} aria-label="Search on Google">
                    <Search size={18} strokeWidth={2} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "var(--gray-500)",
                  display: "block",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                Path
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {location.pathname}
                {location.search}
              </Typography>
            </Box>

            {statusCode != null && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--gray-500)",
                    display: "block",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                  }}
                >
                  Status
                </Typography>
                <Typography variant="body2">
                  {statusCode}
                  {statusText ? ` — ${statusText}` : ""}
                </Typography>
              </Box>
            )}

            {message && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--gray-500)",
                    display: "block",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                  }}
                >
                  Message
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "var(--destructive)",
                    wordBreak: "break-word",
                  }}
                >
                  {message}
                </Typography>
              </Box>
            )}

            {stack && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--gray-500)",
                    display: "block",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                    mb: 0.5,
                  }}
                >
                  Stack Trace
                </Typography>
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    display: "block",
                    overflow: "auto",
                    maxHeight: 450,
                    p: 1.5,
                    bgcolor: "#1e1e1e",
                    color: "#d4d4d4",
                    borderRadius: 1,
                    fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                    fontSize: "0.75rem",
                    lineHeight: 1.4,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    border: "1px solid #333",
                  }}
                >
                  {stack}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<Home size={20} strokeWidth={2} />}
            onClick={goHome}
            sx={{
              bgcolor: "var(--primary, #9d6ac4)",
              "&:hover": { bgcolor: "var(--primary-dark, #7c3aed)" },
              textTransform: "none",
              px: 3,
            }}
          >
            Go to home
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshCw size={20} strokeWidth={2} />}
            onClick={retry}
            sx={{
              borderColor: "var(--gray-400)",
              color: "var(--foreground)",
              textTransform: "none",
              px: 3,
            }}
          >
            Try again
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Error details copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ErrorPage;
